// import { NavList } from "@/components/application/app-navigation/base-components/nav-list";
// import { Input } from "@/components/base/input/input";

// import { SearchLg } from "@untitledui/icons";
import { useNavigate } from "@tanstack/react-router";
import type { FC, ReactNode } from "react";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { useTheme } from "@/contexts/ThemeProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";
import { ViewTransitionTypes, viewTransition } from "@/utils/view-transitions";
import AvatarDropdown from "../AvatarDropdown/AvatarDropdown";

// import { MobileHeaderNav } from "./MobileHeaderNav";

type NavItem = {
	/** Label text for the nav item. */
	label: string;
	/** URL to navigate to when the nav item is clicked. */
	href: string;
	/** Whether the nav item is currently active. */
	current?: boolean;
	/** Icon component to display. */
	icon?: FC<{ className?: string }>;
	/** Badge to display. */
	badge?: ReactNode;
	/** List of sub-items to display. */
	items?: NavItem[];
};

interface HeaderNavigationBaseProps {
	/** List of items to display. */
	items: NavItem[];
	/** List of sub-items to display. */
	subItems?: NavItem[];
	/** Content to display in the trailing position. */
	trailingContent?: ReactNode;
	/** Whether to hide the bottom border. */
	hideBorder?: boolean;
	/** User's name for avatar dropdown */
	name: string;
	/** User's email for avatar dropdown */
	email: string;
	/** Handler for sign out action */
	onSignOut: () => void;
	/** Handler for user profile action */
	onUserProfile: () => void;
	/** URL for the homepage/logo link */
	homeUrl?: string;
	showAvatarDropdown?: boolean;
	/** Avatar image source URL */
	avatarSrc?: string | null;
	leftNavBorderColor?: string;
}

export const HeaderNavigation = ({
	items,
	subItems,
	trailingContent,
	hideBorder = false,
	name,
	email,
	avatarSrc,
	onSignOut,
	onUserProfile,
	homeUrl = "/web/$orgId/home",
	showAvatarDropdown = true,
	leftNavBorderColor,
}: HeaderNavigationBaseProps) => {
	const navigate = useNavigate();
	const activeSubNavItems =
		subItems ||
		items.find((item) => item.current && item.items && item.items.length > 0)
			?.items;

	const showSecondaryNav = activeSubNavItems && activeSubNavItems.length > 0;

	const theme = useTheme();
	const { t } = useAppTranslation();

	return (
		<header className="max-lg:hidden">
			<section
				className={cx(
					"flex h-16 w-full items-center justify-center bg-primary md:h-20 bg-gray-50",
					(!hideBorder || showSecondaryNav) && "border-b border-secondary",
				)}
			>
				<div className="flex w-full justify-between px-4">
					<div className="flex flex-1 items-center gap-4">
						<button
							type="button"
							aria-label={t("main.nav.goToHomepage")}
							onClick={() =>
								navigate({
									to: homeUrl,
									...viewTransition(ViewTransitionTypes.FADE),
								})
							}
							className="w-[288px] flex items-center justify-center px-3 py-2 rounded-lg border-l-4 border transition-colors cursor-pointer"
							style={{
								backgroundColor: "var(--color-logo-bg)",
								borderColor: "var(--color-logo-border)",
								borderLeftColor:
									leftNavBorderColor || "var(--color-logo-border)",
							}}
						>
							<img
								src={
									theme.appTheme === "light"
										? "https://opscdn.blob.core.windows.net/assets/lightBackground/Horizontal Logo/OpSource-Logo-LB-Horizontal-FullColor.png"
										: "https://opscdn.blob.core.windows.net/assets/darkBackground/Horizontal Logo/OpSource-Logo-DB-Horizontal-FullColor.png"
								}
								alt={t("common.logoAlt")}
								className=" object-contain w-[166px]"
							/>
						</button>
					</div>
					{showAvatarDropdown && (
						<div className="flex items-center gap-3">
							{trailingContent}
							<Tooltip
								title={t("main.nav.menu.account.accountMenu")}
								placement="bottom"
								delay={500}
							>
								<AvatarDropdown
									name={name}
									email={email}
									onSignOut={onSignOut}
									onUserProfile={onUserProfile}
									avatarSrc={avatarSrc || undefined}
								/>
							</Tooltip>
						</div>
					)}
				</div>
			</section>
		</header>
	);
};
